/**
 * Attaches the session credential to every API request the app makes.
 *
 * The market-data routes require a token. Roughly 150 fetch call sites reach them, and
 * asking each to remember a header would guarantee that some do not: the ones missed fail
 * as an empty panel rather than an error, which is exactly the kind of bug that ships.
 *
 * So it is done once, here, by wrapping fetch — the same technique the app already uses
 * for response metadata and for the desktop tunnel. A call site added next month is
 * covered without its author knowing this file exists.
 *
 * Only same-origin `/api/` requests are touched. A request that already carries an
 * Authorization header keeps it, so callers that deliberately send something else — a
 * different user's token, a service key — are not overridden.
 */

import axios from 'axios';

import { getMmecAuthHeaderFields } from './authUtils';

/** Same-origin API paths only: never attach our credential to a third-party host. */
function isPlatformApiRequest(url: string): boolean {
    if (!url) return false;
    try {
        const resolved = new URL(url, window.location.origin);
        if (resolved.origin !== window.location.origin) return false;
        return resolved.pathname.startsWith('/api/');
    } catch {
        // Relative paths that URL() cannot parse still route to our own origin.
        return url.startsWith('/api/');
    }
}

function urlFromInput(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    return input?.url || '';
}

function alreadyAuthorised(init: RequestInit | undefined, input: RequestInfo | URL): boolean {
    const fromInit = new Headers(init?.headers || {});
    if (fromInit.has('Authorization')) return true;
    if (input instanceof Request && input.headers.has('Authorization')) return true;
    return false;
}

export function installApiAuthHeaders(): void {
    const g = globalThis as typeof globalThis & { __mmecApiAuthInstalled?: boolean };
    if (g.__mmecApiAuthInstalled) return;
    g.__mmecApiAuthInstalled = true;

    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
        const orig = window.fetch.bind(window);

        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            if (!isPlatformApiRequest(urlFromInput(input)) || alreadyAuthorised(init, input)) {
                return orig(input, init);
            }

            const auth = getMmecAuthHeaderFields();
            if (!Object.keys(auth).length) {
                // Signed out. Let it through and take the 401 — inventing a header here
                // would only turn a clear "not signed in" into a confusing rejection.
                return orig(input, init);
            }

            // A Request object carries its own headers, so merge into a clone rather than
            // into init, which fetch ignores when the first argument is a Request.
            if (input instanceof Request && !init) {
                const headers = new Headers(input.headers);
                Object.entries(auth).forEach(([key, value]) => headers.set(key, value));
                return orig(new Request(input, { headers }));
            }

            const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : {}));
            Object.entries(auth).forEach(([key, value]) => headers.set(key, value));
            return orig(input, { ...init, headers });
        };
    }

    axios.interceptors.request.use((config) => {
        if (!isPlatformApiRequest(config.url || '')) return config;
        if (config.headers?.Authorization) return config;
        const auth = getMmecAuthHeaderFields();
        Object.entries(auth).forEach(([key, value]) => {
            config.headers.set(key, value);
        });
        return config;
    });
}

installApiAuthHeaders();
